import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import Elevation from '@rover/kibble/styles/Elevation';
import type { ApiLocationGeocodeRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiLocationGeocodeRetrieve200';
import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';

import useAddressGeocode from '../hooks/useAddressGeocode';
import useHomeAddress from '../hooks/useHomeAddress';
import { useSuggestionIndices } from '../hooks/useSuggestionIndices';
import useTemporaryAddress from '../hooks/useTemporaryAddress';
import { AddressType, SUGGESTION_QA_ID_PREFIX } from '../LocationInput.constants';
import { SelectOption } from '../LocationInput.types';
import { formatAddress, getSuggestionId, shouldBlurElement } from '../utils';

import AddressModal from './AddressForm/AddressModal';
import HideBoxShadow from './HideBoxShadow';
import HomeAddressOption from './HomeAddressOption';
import SuggestionOption from './SuggestionOption';
import TemporaryAddressOption from './TemporaryAddressOption';

const Suggestions = styled.ul<{
  focused: boolean;
  floatingMenu: boolean;
  menuAlignment: 'left' | 'right';
}>`
  position: absolute;
  bottom: ${({ floatingMenu }) => (floatingMenu ? '-2px' : 0)};
  ${({ menuAlignment }) => menuAlignment}: 0;
  border-radius: ${({ floatingMenu, theme }) =>
    floatingMenu ? theme.radii.secondary : `0 0 ${theme.radii.secondary} ${theme.radii.secondary}`};
  list-style-type: none;
  margin: 0;
  transform: translateY(100%);
  background-color: ${({ theme }) => theme.background.primary};
  z-index: ${ZIndex.DROPDOWN.toNumber()};
  width: 100%;
  padding: 0;
  border: ${({ floatingMenu, focused, theme }) =>
    `1px solid ${
      !floatingMenu && focused ? theme.colors.border.input_focus : theme.colors.border.primary
    }`};
  border-top-width: ${({ floatingMenu }) => (floatingMenu ? '' : 0)};
  overflow: hidden;
  min-width: ${({ floatingMenu }) => (floatingMenu ? '288px' : '')};
  ${Elevation(2)}
`;

const Suggestion = styled.li<{ isActive: boolean }>`
  padding: 0;
  display: flex;
  width: 100%;
  height: 100%;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.space['2x']} ${theme.space['3x']}`};
  transition: background-color 0.15s ease-out;
  text-overflow: ellipsis;
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.background.tertiary : theme.background.primary};
`;

type SuggestionsProps = {
  inputId: string;
  inputValue: string;
  suggestionsMenuAlignment?: 'left' | 'right';
  floatingMenu?: boolean;
  inputFocused: boolean;
  activeIndex: number | null;
  options: SelectOption[];
  handleSelectChange: (
    option: SelectOption,
    newAddressData?: ApiLocationGeocodeRetrieve200 | UserInformationAddress
  ) => void;
  setActiveIndex: (index: number) => void;
  forceUseHomeAddress?: boolean;
  blurInput: () => void;
  enableTwoLineSuggestions?: boolean;
};

const LocationSuggestions: FC<SuggestionsProps> = (props) => {
  const {
    inputId,
    inputValue,
    suggestionsMenuAlignment = 'left',
    floatingMenu,
    inputFocused,
    activeIndex,
    options,
    handleSelectChange,
    setActiveIndex,
    forceUseHomeAddress,
    blurInput,
    enableTwoLineSuggestions = false,
  } = props;

  const { formattedAddress: homeFormattedAddress, address: homeAddress } =
    useHomeAddress(forceUseHomeAddress);
  const { formattedAddress: temporaryFormattedAddress, address: temporaryAddress } =
    useTemporaryAddress();
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const { geocodedAddress, geocodeAddress, resetGeocodedAddress } = useAddressGeocode();

  // Open modal when geocoded address is ready
  useEffect(() => {
    if (geocodedAddress && Object.keys(geocodedAddress).length > 0) {
      setIsAddressModalOpen(true);
    }
  }, [geocodedAddress]);

  const formattedAddress = homeFormattedAddress || '';
  const onSuggestionsButtonClick = useCallback(
    (
      event: React.MouseEvent | React.KeyboardEvent,
      option: SelectOption,
      addressData?: ApiLocationGeocodeRetrieve200 | UserInformationAddress
    ) => {
      event.preventDefault();
      handleSelectChange(option, addressData);
    },
    [handleSelectChange]
  );

  const { homeAddressIndex, temporaryAddressIndex, baseSuggestionsStartIndex } =
    useSuggestionIndices({
      formattedAddress,
      temporaryAddress: temporaryFormattedAddress,
      forceUseHomeAddress,
    });

  const getSuggestionProps = useCallback(
    (
      index: number,
      option: SelectOption,
      addressData?: ApiLocationGeocodeRetrieve200 | UserInformationAddress
    ) => ({
      role: 'option' as const,
      id: getSuggestionId(inputId, index),
      'aria-selected': activeIndex === index,
      tabIndex: 0,
      'data-qa-id': `${SUGGESTION_QA_ID_PREFIX}-${index}`,
      'data-testid': 'suggestion',
      isActive: activeIndex === index,
      onMouseOver: () => setActiveIndex(index),
      onFocus: () => setActiveIndex(index),
      onClick: (event: React.MouseEvent) => onSuggestionsButtonClick(event, option, addressData),
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.stopPropagation();
          if (document.activeElement === event.currentTarget && activeIndex === index) {
            event.preventDefault();
            onSuggestionsButtonClick(event, option, addressData);
          }
        }
      },
    }),
    [inputId, activeIndex, setActiveIndex, onSuggestionsButtonClick]
  );

  const handleSuggestionBlur = (e: React.FocusEvent<HTMLElement>): void => {
    if (shouldBlurElement(e)) blurInput();
  };

  return (
    <>
      <Suggestions
        role="listbox"
        data-qa-id="suggestions"
        data-testid="suggestions"
        floatingMenu={!!floatingMenu}
        focused={inputFocused}
        menuAlignment={suggestionsMenuAlignment}
        id={`${inputId}-listbox`}
      >
        {forceUseHomeAddress && formattedAddress && (
          <Suggestion
            key="home-address-option"
            {...getSuggestionProps(
              homeAddressIndex,
              { value: formattedAddress, label: formattedAddress },
              homeAddress
            )}
            // Override specific props to allow nested interactive elements
            id="home-address-option"
            role="group"
            tabIndex={undefined}
            onClick={undefined}
            onBlur={(e: React.FocusEvent<HTMLElement>) => {
              if (shouldBlurElement(e)) blurInput();
            }}
          >
            <HomeAddressOption
              blurInput={blurInput}
              formattedAddress={formattedAddress}
              forceUseHomeAddress={forceUseHomeAddress}
              onSelectAddress={(event: React.MouseEvent | React.KeyboardEvent) => {
                onSuggestionsButtonClick(
                  event,
                  { label: formattedAddress, value: formattedAddress },
                  homeAddress
                );
              }}
              onEdit={(newAddressData) => {
                const newFormattedAddress = formatAddress(newAddressData);
                handleSelectChange(
                  { label: newFormattedAddress, value: newFormattedAddress },
                  newAddressData
                );
              }}
            />
          </Suggestion>
        )}
        {temporaryAddress && forceUseHomeAddress && (
          <Suggestion
            key="temporary-address-option"
            {...getSuggestionProps(
              temporaryAddressIndex,
              {
                value: temporaryFormattedAddress,
                label: temporaryFormattedAddress,
              },
              temporaryAddress
            )}
            // Override specific props to allow nested interactive elements
            id="temporary-address-option"
            role="group"
            tabIndex={undefined}
            onClick={undefined}
            onBlur={(e: React.FocusEvent<HTMLElement>) => {
              if (shouldBlurElement(e)) blurInput();
            }}
          >
            <TemporaryAddressOption
              blurInput={blurInput}
              onSelectAddress={(event: React.MouseEvent | React.KeyboardEvent) =>
                onSuggestionsButtonClick(
                  event,
                  { label: temporaryFormattedAddress, value: temporaryFormattedAddress },
                  temporaryAddress
                )
              }
              onEdit={(newTemporaryAddress) => {
                const newFormattedAddress = formatAddress(newTemporaryAddress);
                handleSelectChange(
                  { label: newFormattedAddress, value: newFormattedAddress },
                  newTemporaryAddress
                );
              }}
            />
          </Suggestion>
        )}
        {options.map((option, idx) => {
          const index = baseSuggestionsStartIndex + idx;
          const suggestionProps = getSuggestionProps(index, option);
          const {
            onClick: defaultOnClick,
            onKeyDown: defaultOnKeyDown,
            ...restSuggestionProps
          } = suggestionProps;

          const handleSuggestionButtonClickWithGeocode = (
            event: React.MouseEvent | React.KeyboardEvent
          ): void => {
            event.preventDefault();
            geocodeAddress(option.placeId);
          };

          const handleClick = forceUseHomeAddress
            ? handleSuggestionButtonClickWithGeocode
            : defaultOnClick;

          const handleKeyDown = forceUseHomeAddress
            ? (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  // Always stop propagation to prevent bubbling to parent handlers
                  event.stopPropagation();

                  // Only trigger selection if this element actually has focus
                  if (document.activeElement === event.currentTarget) {
                    event.preventDefault();
                    handleSuggestionButtonClickWithGeocode(event);
                  }
                }
              }
            : defaultOnKeyDown;

          return (
            <Suggestion
              key={option.label as string}
              {...restSuggestionProps}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              onBlur={handleSuggestionBlur}
            >
              <SuggestionOption
                label={option.label as string}
                inputValue={inputValue}
                structuredFormatting={option.structuredFormatting}
                enableTwoLineDisplay={enableTwoLineSuggestions}
              />
            </Suggestion>
          );
        })}
      </Suggestions>

      {/* render element to hide box shadow that appears between the
          input and suggestions to make it appear like one box shadow */}
      {!floatingMenu && <HideBoxShadow />}

      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => {
          setIsAddressModalOpen(false);
          resetGeocodedAddress();
          blurInput();
        }}
        onEdit={(newAddressData) => {
          const newFormattedAddress = formatAddress(newAddressData);
          handleSelectChange(
            { label: newFormattedAddress ?? '', value: newFormattedAddress ?? '' },
            newAddressData
          );
        }}
        addressType={AddressType.Temporary}
        address={geocodedAddress}
      />
    </>
  );
};

export default LocationSuggestions;
